import { frontendURL } from 'dashboard/helper/URLHelper';
import { buildTelLink, buildWhatsappLink } from 'shared/helpers/mapLinks';
import { toText } from './primitiveHelpers';

export const LAIS_CHANNELS = Object.freeze({
  PHONE_DIALER: 'phone_dialer',
  CHATWOOT_CONVERSATION: 'chatwoot_conversation',
  WHATSAPP: 'whatsapp',
  ERROR: 'error',
});

const conversationUrl = ({ accountId, conversationId }) =>
  accountId && conversationId
    ? frontendURL(`accounts/${accountId}/conversations/${conversationId}`)
    : '';

const contactUrl = ({ accountId, contactId }) =>
  accountId && contactId
    ? frontendURL(`accounts/${accountId}/contacts/${contactId}`)
    : '';

export const resolveChannel = ({
  accountId,
  phone,
  conversationId,
  contactId,
  inboxId,
  text,
  preferDialer = false,
} = {}) => {
  if (preferDialer && phone) {
    return {
      channel: LAIS_CHANNELS.PHONE_DIALER,
      action: 'open_external_url',
      url: buildTelLink(phone),
      text,
    };
  }

  const internalConversationUrl = conversationUrl({
    accountId,
    conversationId,
  });
  if (internalConversationUrl) {
    return {
      channel: LAIS_CHANNELS.CHATWOOT_CONVERSATION,
      action: 'open_internal_route',
      url: internalConversationUrl,
      text,
    };
  }

  const internalContactUrl = contactUrl({ accountId, contactId });
  if (internalContactUrl && inboxId) {
    return {
      channel: LAIS_CHANNELS.CHATWOOT_CONVERSATION,
      action: 'open_internal_route',
      url: internalContactUrl,
      text,
      payload: { contactId, inboxId },
    };
  }

  const whatsappUrl = buildWhatsappLink(phone, text);
  if (whatsappUrl) {
    return {
      channel: LAIS_CHANNELS.WHATSAPP,
      action: 'open_external_url',
      url: whatsappUrl,
      text,
    };
  }

  return {
    channel: LAIS_CHANNELS.ERROR,
    action: 'show_error',
    error: 'Nenhum canal disponível para iniciar a conversa.',
    text,
  };
};

export const ownerContactChannelPreference = accountConfig =>
  [
    accountConfig?.ownerContactChannel,
    accountConfig?.owner_contact_channel,
    accountConfig?.laisOwnerContactChannel,
    accountConfig?.lais_owner_contact_channel,
  ].find(value => ['internal', 'whatsapp'].includes(value)) || 'whatsapp';

export const resolveOwnerChannel = ({
  accountId,
  visit,
  accountConfig,
  text,
} = {}) => {
  const owner =
    visit?.ownerContact ||
    visit?.owner_contact ||
    visit?.imovel?.ownerContact ||
    visit?.imovel?.owner_contact ||
    {};
  const attrs =
    visit?.additionalAttributes || visit?.additional_attributes || {};
  const phone =
    owner.phoneNumber ||
    owner.phone_number ||
    attrs.ownerPhone ||
    attrs.owner_phone ||
    visit?.imovel?.ownerPhone ||
    visit?.imovel?.owner_phone;
  const preference = ownerContactChannelPreference(accountConfig);

  return resolveChannel({
    accountId,
    phone,
    text,
    conversationId:
      preference === 'internal'
        ? owner.conversationId ||
          owner.conversation_id ||
          attrs.ownerConversationId
        : null,
    contactId:
      preference === 'internal'
        ? owner.contactId ||
          owner.contact_id ||
          owner.id ||
          attrs.ownerContactId
        : null,
    inboxId:
      preference === 'internal'
        ? owner.inboxId || owner.inbox_id || attrs.ownerInboxId
        : null,
  });
};

export const channelTrackingPayload = resolvedChannel => ({
  channel: resolvedChannel?.channel || LAIS_CHANNELS.ERROR,
  action: resolvedChannel?.action || 'unknown',
  usedInternalConversation:
    resolvedChannel?.channel === LAIS_CHANNELS.CHATWOOT_CONVERSATION,
});

export const openResolvedChannel = ({ resolvedChannel, router, win } = {}) => {
  if (!resolvedChannel?.url) return false;

  if (resolvedChannel.action === 'open_internal_route') {
    router?.push?.(resolvedChannel.url);
    return true;
  }

  const currentWindow = win || (typeof window === 'undefined' ? null : window);
  currentWindow?.open?.(resolvedChannel.url, '_blank', 'noopener');
  return Boolean(currentWindow);
};

export const channelErrorMessage = resolvedChannel =>
  toText(resolvedChannel?.error) ||
  'Não foi possível abrir um canal para esta conversa.';
